import type {
  CreateStockAdjustmentRequest,
  CreateStockCountRequest,
  CreateStockTransferRequest,
  CreateWarehouseRequest,
  ManualAdjustmentRequest,
  ReceiveTransferRequest,
  StockAdjustmentResponse,
  StockCountResponse,
  StockLevelRow,
  StockMovementRow,
  StockMovementSummary,
  StockSummary,
  StockTransferResponse,
  UpdateStockAdjustmentRequest,
  UpdateStockCountRequest,
  UpdateStockTransferRequest,
  UpdateWarehouseRequest,
  WarehouseResponse,
} from '@palaccount/contracts';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_ALL } from '@palaccount/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/api';
import { throwApiError } from '@/lib/server-messages';

import { ITEMS_KEY } from './api';

/**
 * The stock layer's data hooks (MD-08/INV-04 warehouses, INV-05 levels and
 * counts, INV-06 movements, INV-07 transfers, INV-08 vouchers) — the same
 * thin TanStack Query wrappers over the typed contract client that api.ts
 * uses for the item master, kept apart so that file stays item-master-only.
 *
 * Every write moves balances somewhere, so the mutations invalidate the whole
 * stock surface AND the item catalog (whose rows now carry live on-hand).
 */

export const WAREHOUSES_KEY = ['warehouses'] as const;
export const STOCK_LEVELS_KEY = ['stock-levels'] as const;
export const MOVEMENTS_KEY = ['stock-movements'] as const;
export const ADJUSTMENTS_KEY = ['stock-adjustments'] as const;
export const COUNTS_KEY = ['stock-counts'] as const;
export const TRANSFERS_KEY = ['stock-transfers'] as const;

/** Anything that writes a movement moves every figure on every stock screen. */
function useInvalidateStock() {
  const queryClient = useQueryClient();
  return () => {
    for (const key of [
      WAREHOUSES_KEY,
      STOCK_LEVELS_KEY,
      MOVEMENTS_KEY,
      ADJUSTMENTS_KEY,
      COUNTS_KEY,
      TRANSFERS_KEY,
      ITEMS_KEY,
    ]) {
      void queryClient.invalidateQueries({ queryKey: key });
    }
  };
}

// ── warehouses (MD-08 / INV-04) ─────────────────────────────────────────────

export interface WarehouseFilters {
  q?: string | undefined;
  status?: 'active' | 'inactive' | undefined;
}

export function useWarehouses(filters: WarehouseFilters = {}) {
  return useQuery({
    queryKey: [...WAREHOUSES_KEY, filters],
    queryFn: async (): Promise<WarehouseResponse[]> => {
      const response = await api.warehouses.list({
        query: {
          ...(filters.q ? { q: filters.q } : {}),
          ...(filters.status ? { status: filters.status } : {}),
        },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body.warehouses;
    },
  });
}

export function useWarehouse(warehouseId: string | undefined) {
  return useQuery({
    queryKey: [...WAREHOUSES_KEY, 'one', warehouseId],
    enabled: Boolean(warehouseId),
    queryFn: async (): Promise<WarehouseResponse> => {
      const response = await api.warehouses.get({ params: { warehouseId: warehouseId! } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function useWarehouseMutations() {
  const invalidate = useInvalidateStock();
  const create = useMutation({
    mutationFn: async (body: CreateWarehouseRequest): Promise<WarehouseResponse> => {
      const response = await api.warehouses.create({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: async ({
      warehouseId,
      body,
    }: {
      warehouseId: string;
      body: UpdateWarehouseRequest;
    }): Promise<WarehouseResponse> => {
      const response = await api.warehouses.update({ params: { warehouseId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const setActive = useMutation({
    mutationFn: async ({
      warehouseId,
      active,
    }: {
      warehouseId: string;
      active: boolean;
    }): Promise<WarehouseResponse> => {
      const response = active
        ? await api.warehouses.reactivate({ params: { warehouseId }, body: {} })
        : await api.warehouses.deactivate({ params: { warehouseId }, body: {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: async (warehouseId: string): Promise<void> => {
      const response = await api.warehouses.remove({ params: { warehouseId } });
      if (response.status !== 204) throwApiError(response.status, response.body);
    },
    onSuccess: invalidate,
  });
  return { create, update, setActive, remove };
}

// ── stock levels (INV-05) ───────────────────────────────────────────────────

export interface StockLevelFilters {
  q?: string | undefined;
  status?: 'ok' | 'low' | 'out' | 'over' | undefined;
  /** Any-of (BR-INV-05-06); empty = no constraint. */
  warehouseIds?: string[] | undefined;
  categoryId?: string | undefined;
  /** bugs: ST-02 — one maker's items (IT-S6). */
  manufacturerId?: string | undefined;
  /** bugs: Stock levels — Tracked / Untracked on Track stock alerts (IT-L3). */
  alerts?: 'tracked' | 'untracked' | undefined;
  includeInactive?: boolean | undefined;
  /**
   * BR-INV-05-08 — §5.2 field 13's three ADDED filters. They ride the same
   * request as every other filter, which is what ERR-INV-05-02 demands the
   * negation of: the prototype collected these three client-side and never
   * let them near the query.
   */
  supplierId?: string | undefined;
  taxCategory?: string | undefined;
  unitId?: string | undefined;
}

export interface StockLevelsResult {
  rows: StockLevelRow[];
  summary: StockSummary;
  total: number;
  page: number;
  pageSize: number;
}

/**
 * INV-05's balances, one page at a time (NFR-42) — 50,000 items.
 *
 * `pageSize` accepts the opt-out because ONE caller is not a list screen: the
 * transfer editor reads live availability at the source warehouse to make the
 * §5.5 over-transfer warning work while a clerk is still typing, and a paged
 * read there would silently fail to warn about an item past the cut. It is
 * named in `UNPAGED_CALLERS`.
 */
export function useStockLevels(
  filters: StockLevelFilters,
  page: number,
  pageSize: number | typeof PAGE_SIZE_ALL,
) {
  return useQuery({
    queryKey: [...STOCK_LEVELS_KEY, filters, page, pageSize],
    queryFn: async (): Promise<StockLevelsResult> => {
      const response = await api.stock.levels({
        query: {
          ...(filters.q ? { q: filters.q } : {}),
          ...(filters.status ? { status: filters.status } : {}),
          ...(filters.warehouseIds && filters.warehouseIds.length > 0
            ? { warehouseIds: filters.warehouseIds.join(',') }
            : {}),
          ...(filters.categoryId ? { categoryId: filters.categoryId } : {}),
          ...(filters.manufacturerId ? { manufacturerId: filters.manufacturerId } : {}),
          ...(filters.alerts ? { alerts: filters.alerts } : {}),
          ...(filters.includeInactive ? { includeInactive: 'true' as const } : {}),
          ...(filters.supplierId ? { supplierId: filters.supplierId } : {}),
          ...(filters.taxCategory?.trim() ? { taxCategory: filters.taxCategory.trim() } : {}),
          ...(filters.unitId ? { unitId: filters.unitId } : {}),
          page,
          pageSize,
        },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

/**
 * BR-INV-05-10 — the clock icon's modal: one item's history, newest first,
 * one page at a time (NFR-42). "One item's rows" is the same append-only
 * table as INV-06, narrowed rather than bounded — a fast-moving SKU accrues
 * thousands on its own.
 */
export function useItemMovements(itemId: string | undefined, page = 1, pageSize = 25) {
  return useQuery({
    queryKey: [...MOVEMENTS_KEY, 'item', itemId, page, pageSize],
    enabled: Boolean(itemId),
    queryFn: async () => {
      const response = await api.stock.itemMovements({
        params: { itemId: itemId! },
        query: { page, pageSize },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

// ── stock movements (INV-06) ────────────────────────────────────────────────

export interface MovementFilters {
  q?: string | undefined;
  reference?: string | undefined;
  dateFrom?: string | undefined;
  dateTo?: string | undefined;
  kinds?: string[] | undefined;
  warehouseIds?: string[] | undefined;
  categoryId?: string | undefined;
}

export interface MovementsResult {
  rows: StockMovementRow[];
  summary: StockMovementSummary;
  total: number;
  page: number;
  pageSize: number;
}

/**
 * INV-06's ledger, ONE PAGE AT A TIME (NFR-42) — the screen the acceptance
 * sweep measured at 54.9 s with 60,397 `<tr>` in the DOM.
 *
 * `keepPreviousData`: a movement ledger is read row by row, and a Next that
 * blinked back to a skeleton would lose the reader's place every time.
 */
export function useStockMovements(filters: MovementFilters, page: number, pageSize: number) {
  return useQuery({
    queryKey: [...MOVEMENTS_KEY, filters, page, pageSize],
    queryFn: async (): Promise<MovementsResult> => {
      const response = await api.stock.movements({
        query: {
          ...(filters.q ? { q: filters.q } : {}),
          ...(filters.reference ? { reference: filters.reference } : {}),
          ...(filters.dateFrom ? { dateFrom: filters.dateFrom } : {}),
          ...(filters.dateTo ? { dateTo: filters.dateTo } : {}),
          ...(filters.kinds && filters.kinds.length > 0 ? { kinds: filters.kinds.join(',') } : {}),
          ...(filters.warehouseIds && filters.warehouseIds.length > 0
            ? { warehouseIds: filters.warehouseIds.join(',') }
            : {}),
          ...(filters.categoryId ? { categoryId: filters.categoryId } : {}),
          page,
          pageSize,
        },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

/**
 * BR-INV-06-06/-07/-08 — the hand-written movement, amber in the ledger.
 * MovementS: one adjustment of a serial-tracked item is one movement per
 * unit, so what comes back is the whole split, not its first row.
 */
export function useManualAdjust() {
  const invalidate = useInvalidateStock();
  return useMutation({
    mutationFn: async (body: ManualAdjustmentRequest): Promise<StockMovementRow[]> => {
      const response = await api.stock.manualAdjust({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body.rows;
    },
    onSuccess: invalidate,
  });
}

/** BR-INV-06-02's only correction path — a movement is never edited. */
export function useCorrectMovement() {
  const invalidate = useInvalidateStock();
  return useMutation({
    mutationFn: async ({
      movementId,
      reason,
    }: {
      movementId: string;
      reason: string;
    }): Promise<StockMovementRow> => {
      const response = await api.stock.correct({ params: { movementId }, body: { reason } });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
}

// ── stock adjustment vouchers (INV-08) ──────────────────────────────────────

export interface AdjustmentFilters {
  direction?: 'in' | 'out' | undefined;
  status?: 'draft' | 'posted' | 'cancelled' | undefined;
  warehouseId?: string | undefined;
  /** PRJ-01: the vouchers issued to one project. */
  projectId?: string | undefined;
}

/** INV-08's register, one page at a time (NFR-42). All four filters in SQL. */
export function useStockAdjustments(
  filters: AdjustmentFilters = {},
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
) {
  return useQuery({
    queryKey: [...ADJUSTMENTS_KEY, filters, page, pageSize],
    queryFn: async () => {
      const response = await api.stockAdjustments.list({
        query: {
          ...(filters.direction ? { direction: filters.direction } : {}),
          ...(filters.status ? { status: filters.status } : {}),
          ...(filters.warehouseId ? { warehouseId: filters.warehouseId } : {}),
          ...(filters.projectId ? { projectId: filters.projectId } : {}),
          page,
          pageSize,
        },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

export function useStockAdjustment(adjustmentId: string | undefined) {
  return useQuery({
    queryKey: [...ADJUSTMENTS_KEY, 'one', adjustmentId],
    enabled: Boolean(adjustmentId),
    queryFn: async (): Promise<StockAdjustmentResponse> => {
      const response = await api.stockAdjustments.get({
        params: { adjustmentId: adjustmentId! },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function useStockAdjustmentMutations(adjustmentId: string) {
  const invalidate = useInvalidateStock();
  const create = useMutation({
    mutationFn: async (body: CreateStockAdjustmentRequest): Promise<StockAdjustmentResponse> => {
      const response = await api.stockAdjustments.create({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: async (body: UpdateStockAdjustmentRequest): Promise<StockAdjustmentResponse> => {
      const response = await api.stockAdjustments.update({ params: { adjustmentId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: async (): Promise<void> => {
      const response = await api.stockAdjustments.remove({ params: { adjustmentId } });
      if (response.status !== 204) throwApiError(response.status, response.body);
    },
    onSuccess: invalidate,
  });
  const approve = useMutation({
    mutationFn: async (decision: 'approved' | 'rejected'): Promise<StockAdjustmentResponse> => {
      const response = await api.stockAdjustments.approve({
        params: { adjustmentId },
        body: { decision },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const post = useMutation({
    mutationFn: async (): Promise<StockAdjustmentResponse> => {
      const response = await api.stockAdjustments.post({ params: { adjustmentId }, body: {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const cancel = useMutation({
    /** GEN-59: the cancel carries its typed reason. */
    mutationFn: async (reason: string): Promise<StockAdjustmentResponse> => {
      const response = await api.stockAdjustments.cancel({
        params: { adjustmentId },
        body: { reason },
      });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  return { create, update, remove, approve, post, cancel };
}

// ── stock counts (INV-05 §5.7) ──────────────────────────────────────────────

/** INV-05 §5.7's history, one page at a time (NFR-42). */
export function useStockCounts(page = 1, pageSize = DEFAULT_PAGE_SIZE) {
  return useQuery({
    queryKey: [...COUNTS_KEY, 'list', page, pageSize],
    queryFn: async () => {
      const response = await api.stockCounts.list({ query: { page, pageSize } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

export function useStockCount(countId: string | undefined) {
  return useQuery({
    queryKey: [...COUNTS_KEY, 'one', countId],
    enabled: Boolean(countId),
    queryFn: async (): Promise<StockCountResponse> => {
      const response = await api.stockCounts.get({ params: { countId: countId! } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function useStockCountMutations(countId: string) {
  const invalidate = useInvalidateStock();
  const create = useMutation({
    mutationFn: async (body: CreateStockCountRequest): Promise<StockCountResponse> => {
      const response = await api.stockCounts.create({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: async (body: UpdateStockCountRequest): Promise<StockCountResponse> => {
      const response = await api.stockCounts.update({ params: { countId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const post = useMutation({
    mutationFn: async (): Promise<StockCountResponse> => {
      const response = await api.stockCounts.post({ params: { countId }, body: {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const cancel = useMutation({
    /** GEN-59: the cancel carries its typed reason. */
    mutationFn: async (reason: string): Promise<StockCountResponse> => {
      const response = await api.stockCounts.cancel({ params: { countId }, body: { reason } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  return { create, update, post, cancel };
}

// ── stock transfers (INV-07) ────────────────────────────────────────────────

/** INV-07's register, one page at a time (NFR-42). */
export function useStockTransfers(page = 1, pageSize = DEFAULT_PAGE_SIZE) {
  return useQuery({
    queryKey: [...TRANSFERS_KEY, 'list', page, pageSize],
    queryFn: async () => {
      const response = await api.stockTransfers.list({ query: { page, pageSize } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    placeholderData: keepPreviousData,
  });
}

export function useStockTransfer(transferId: string | undefined) {
  return useQuery({
    queryKey: [...TRANSFERS_KEY, 'one', transferId],
    enabled: Boolean(transferId),
    queryFn: async (): Promise<StockTransferResponse> => {
      const response = await api.stockTransfers.get({ params: { transferId: transferId! } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
  });
}

export function useStockTransferMutations(transferId: string) {
  const invalidate = useInvalidateStock();
  const create = useMutation({
    mutationFn: async (body: CreateStockTransferRequest): Promise<StockTransferResponse> => {
      const response = await api.stockTransfers.create({ body });
      if (response.status !== 201) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: async (body: UpdateStockTransferRequest): Promise<StockTransferResponse> => {
      const response = await api.stockTransfers.update({ params: { transferId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: async (): Promise<void> => {
      const response = await api.stockTransfers.remove({ params: { transferId } });
      if (response.status !== 204) throwApiError(response.status, response.body);
    },
    onSuccess: invalidate,
  });
  const ship = useMutation({
    mutationFn: async (): Promise<StockTransferResponse> => {
      const response = await api.stockTransfers.ship({ params: { transferId }, body: {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const complete = useMutation({
    mutationFn: async (): Promise<StockTransferResponse> => {
      const response = await api.stockTransfers.complete({ params: { transferId }, body: {} });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const receive = useMutation({
    mutationFn: async (body: ReceiveTransferRequest): Promise<StockTransferResponse> => {
      const response = await api.stockTransfers.receive({ params: { transferId }, body });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  const cancel = useMutation({
    /** GEN-59: the cancel carries its typed reason. */
    mutationFn: async (reason: string): Promise<StockTransferResponse> => {
      const response = await api.stockTransfers.cancel({ params: { transferId }, body: { reason } });
      if (response.status !== 200) throwApiError(response.status, response.body);
      return response.body;
    },
    onSuccess: invalidate,
  });
  return { create, update, remove, ship, complete, receive, cancel };
}
